import { AppControlComponent } from '@common/components/app-control/app-control.component';
import { SaveEvent } from '@progress/kendo-angular-grid';
import { VesselVisitDocumentPermissions } from '@common/classes/permissions';
import { Component, OnInit, ViewChild } from '@angular/core';
import { BaseViewComponent } from '@common/classes/base-view';
import { CommonService } from '@common/services/common.service';
import { ViewMode } from '@common/models/view-mode';
import { ApproveOperationalPlanType } from '@common/classes/approve-operational-plan-type';
import { ReceiveOperationalPlanType } from '@common/classes/receive-operational-plan-type';
import _ from 'lodash';
import { VesselVisitDocumentTypeCodes } from '@common/classes/vessel-visit-document-types';
import { VesselVisitDocumentStatusCodes } from '@common/classes/vessel-visit-document-status-codes';
import { TranslateService } from '@ngx-translate/core';
import { VesselVisitDocumentEditableCheckService } from '@common/services/vessel-visit-document-editable-check-service';

@Component({
    selector: 'app-vessel-visit-document-view',
    templateUrl: './vessel-visit-document-view.component.html',
    styleUrls: ['./vessel-visit-document-view.component.scss'],
    providers: [CommonService]
})
export class VesselVisitDocumentViewComponent extends BaseViewComponent implements OnInit {
    entityName = 'VesselVisitDocument';
    breadcrumb = [];
    override arrayProperties = ['cargo', 'freightForwarders', 'agents', 'berths', 'documents', 'operations', 'standstills', 'movements'];

    VesselVisitDocumentPermissions = VesselVisitDocumentPermissions; // for HTML binding
    editPermission = VesselVisitDocumentPermissions.Action.Edit;
    createPermission = VesselVisitDocumentPermissions.Action.Create;
    freightForwarderOptions: any[] = [];
    cargoAgentOptions: any[] = [];
    departureAgentOptions: any[] = [];
    lastMovementTypeId: string;
    vesselCategoryId: any;
    cargoTypes: any[] = [];
    packagingTypes: any[] = [];

    VesselVisitDocumentTypeCodes = VesselVisitDocumentTypeCodes;
    VesselVisitDocumentStatusCodes = VesselVisitDocumentStatusCodes;

    @ViewChild('berthControl', { static: false }) berthControl: AppControlComponent;

    private vesselVisitDocumentEditableCheckService: VesselVisitDocumentEditableCheckService;

    constructor(
        commonService: CommonService,
        private translate: TranslateService,
    ) {
        super(commonService);

        this.vesselVisitDocumentEditableCheckService = this.common.vesselVisitDocumentEditableCheckService;

        this.actionBar.push({
            label: 'Actions',
            items: [
               {
                    label: 'Approve',
                    icon: 'check',
                    isVisible: () => this.canApprove(),
                    onClick: () => this.approve(ApproveOperationalPlanType.DEFAULT)
                },
                {
                    label: this.resolveApproveLabel('Approve agency'),
                    icon: 'check',
                    isVisible: () => this.canApproveAgent(),
                    onClick: () => this.approve(ApproveOperationalPlanType.AGENT)
                },
                {
                    label: this.resolveApproveLabel('Approve dispatch'),
                    icon: 'check',
                    isVisible: () => this.canApproveDispatch(),
                    onClick: () => this.approve(ApproveOperationalPlanType.DISPATCH)
                },
                {
                    label: this.resolveApproveLabel('Approve dispatch shift'),
                    icon: 'check',
                    isVisible: () => this.canApproveDispatchShift(),
                    onClick: () => this.approve(ApproveOperationalPlanType.DISPATCH_SHIFT)
                },
                {
                    label: this.resolveReceiveLabel('Receive FSPD'),
                    icon: 'envelope',
                    isVisible: () => this.canReceiveFSPD(),
                    onClick: () => this.receive(ReceiveOperationalPlanType.FSPD)
                },
                {
                    label: this.resolveReceiveLabel('Receive captain'),
                    icon: 'envelope',
                    isVisible: () => this.canReceiveCaptain(),
                    onClick: () => this.receive(ReceiveOperationalPlanType.CAPTAIN)
                },
                {
                    label: 'Reject',
                    icon: 'xmark',
                    isVisible: () => this.canReject(),
                    onClick: () => this.reject()
                },
                {
                    label: 'Cancel',
                    icon: 'ban',
                    isVisible: () => this.canCancelAccommodationRequest(),
                    onClick: () => this.cancelAccommodationRequest()
                }
            ],
            permissions: [VesselVisitDocumentPermissions.Action]
        });
    }

    override getIdentifier(){
        //this.translateService.instant(this.entityName.replace(/([A-Z])/g, ' $1').trim());
        return `${this.model.documentType.id} - ${this.model.documentType.name}`
    };

    canCancelAccommodationRequest(): boolean {
        if(this.model){
            if (this.model.documentTypeId === VesselVisitDocumentTypeCodes.AR &&
                this.model.statusId === VesselVisitDocumentStatusCodes.WaitingAgentApproval &&
                this.model.accommodationRequests?.length > 0 &&
                this.model.accommodationRequests[0].berthProposals?.length > 0) {
                   const lastProposal = this.model.accommodationRequests[0].berthProposals
                    .reduce((prev, curr) => (curr.id > prev.id ? curr : prev));
                    
                return lastProposal?.accepted == false;
            }
        }
        return false;
    }

    override canEdit() {
        //check user permissions first
        const hasEditPermission = super.hasEditPermission();

        if(!hasEditPermission){
            return false;
        }

        return this.isEditable();
    }
    
    isEditable(){
        if (this.model) {            
            return this.vesselVisitDocumentEditableCheckService.isEditable(this.model, this.user);
        }
        return false;
    }

    async ngOnInit() {
        super.ngOnInit();
        const { results: allOrganizations } = await this.common.cqrs.query('Organizations', { take: 1000, permissionFilter: false }) as any;
        const mapToCodelist = (orgs: any[]) => orgs.map(({ id: value, name: label }) => ({ value, label }));
        
        this.freightForwarderOptions = mapToCodelist(allOrganizations.filter(org => org.roles.some(r => r.roleId === 4))); // freight forwarders
        this.cargoAgentOptions = mapToCodelist(allOrganizations.filter(org => org.typeId === 'AGN')); // cargo agents
        this.departureAgentOptions = mapToCodelist(allOrganizations.filter(org => org.roles.some(r => r.roleId === 3))); // vessel agents
    }

    override async modelLoaded(): Promise<void> {
        super.modelLoaded();        

        // run some checks and display error
        if(this.model.documentTypeId === VesselVisitDocumentTypeCodes.AR && this.model.accommodationRequests?.length==0){            
            this.toastrService.error(this.translateService.instant('Accommodation request is missing! Please contact system administrator'));
            this.router.navigate(['/vessel-visit/list']);
            return;
        }else if(this.model.documentTypeId === VesselVisitDocumentTypeCodes.OP && this.model.operationalPlans?.length==0){
            this.toastrService.error(this.translateService.instant('Operational plan is missing! Please contact system administrator'));
            this.router.navigate(['/vessel-visit/list']);
            return;
        }

        this.breadcrumb = [
            {
                icon: 'ship',
                title: 'Vessel Visits',
                route: '/vessel-visit/list'
            },
            {
                icon: 'ship',
                title: 'Vessel Visit View',
                route:  `/vessel-visit/view/${this.model.vesselVisit.id}`
            },
            {
                icon: 'file',
                title: 'Vessel Visit Document View'
            }
        ];
    }

    override async afterSave(model: any) {
        // In case status refresh needed on view mode
        if (this.mode === ViewMode.view) await this.initialize();
        super.afterSave(model);
    }

    override navigateToEditMode(model?: any): void {
        // if (!this.user.isSystemUser && addHours(new Date(this.model.eta), 12).getTime() <= new Date().getTime()) {
        //     this.toastrService.error(this.translateService.instant('It is not possible to edit the data of the selected vessel visit because the ETA is less than 12 hours from the current time'));
        //     return;
        // }
        super.navigateToEditMode(model);
    }    

    onRowSave(event: SaveEvent) {
        event.dataItem.vesselVisitId = this.model.id;
    }

    canEditRow(row: any){
        const isNew = !row?.id;
        const sameOrganization = row?.createdBy?.organizationId === this.user.organizationId;
        const isAdmin = this.isAdminUser();

        return (isNew || sameOrganization || isAdmin);  
    }

    canApprove() {        
        if (this.model 
            && this.model.documentTypeId !== VesselVisitDocumentTypeCodes.OP 
            && this.model.documentTypeId !== VesselVisitDocumentTypeCodes.AR) 
        {            
            const hasPermission = this.user.hasPermission(VesselVisitDocumentPermissions.Action.Approve);         
            const validStatus = 
                this.model?.statusId === VesselVisitDocumentStatusCodes.WaitingApproval;

            return hasPermission && validStatus;  
        }
        return false;
    }

    canApproveAgent() {        
        if (this.model && this.model.documentTypeId === VesselVisitDocumentTypeCodes.OP) {                     
            if (this.model.operationalPlans?.length > 0 && this.model.operationalPlans[0].agencyApprovalDate == null) {
                const hasPermission = this.user.hasPermission(VesselVisitDocumentPermissions.Action.ApproveAgent);         
                const validStatus = 
                    this.model?.statusId === VesselVisitDocumentStatusCodes.WaitingApproval;

                return hasPermission && validStatus;  
            }
        }
        return false;
    }

    canApproveDispatch() {        
        if (this.model && this.model.documentTypeId === VesselVisitDocumentTypeCodes.OP) { 
            if (this.model.operationalPlans?.length > 0 && this.model.operationalPlans[0].dispatchApprovalDate == null) {
                const hasPermission = this.user.hasPermission(VesselVisitDocumentPermissions.Action.ApproveDispatch);             
                const validStatus = 
                    this.model?.statusId === VesselVisitDocumentStatusCodes.WaitingApproval;

                return hasPermission && validStatus;  
            }
        }
        return false;
    }

    canApproveDispatchShift() {
        if (this.model && this.model.documentTypeId === VesselVisitDocumentTypeCodes.OP) {     
            if (this.model.operationalPlans?.length > 0 && this.model.operationalPlans[0].dispatchShiftApprovalDate == null) { 
                const hasPermission = this.user.hasPermission(VesselVisitDocumentPermissions.Action.ApproveDispatchShift);          
                const validStatus = 
                    this.model?.statusId === VesselVisitDocumentStatusCodes.WaitingApproval;

                return hasPermission && validStatus;  
            }
        }
        return false;
    }

    async approve(approveType: ApproveOperationalPlanType) {
        var title = this.translateService.instant('Vessel Visit Document Approval');
        var message = this.translateService.instant('Are you sure you want to approve this vessel visit document?');

        if (approveType!=ApproveOperationalPlanType.DEFAULT) {
            title = this.translateService.instant("Approve Operational Plan");
            message = this.translateService.instant("Are you sure you want to approve this operational plan?");
        }

        const data = await this.dialogService.form({
            title: title,
            message: message,
            properties: []
        });
        if (!data) return;
        this.executeCommand('ApproveVesselVisitDocument', { ApproveType: approveType });
    }

    canReceiveFSPD() {        
        if (this.model && this.model.documentTypeId == VesselVisitDocumentTypeCodes.OP) { 
            if (this.model.operationalPlans?.length > 0 && this.model.operationalPlans[0].fspdReceivedDate == null) {
                const hasPermission = this.user.hasPermission(VesselVisitDocumentPermissions.Action.ReceiveFSPD);           
                const sameOrganization = this.model?.createdBy?.organizationId === this.user.organizationId;            
                const validStatus = 
                    this.model?.statusId === VesselVisitDocumentStatusCodes.Approved;

                const fieldsCheck =
                    this.model.operationalPlans[0]?.agencyApprovedById != null &&
                    this.model.operationalPlans[0]?.agencyApprovalDate != null &&
                    this.model.operationalPlans[0]?.dispatchApprovedById != null &&
                    this.model.operationalPlans[0]?.dispatchApprovalDate != null &&
                    this.model.operationalPlans[0]?.dispatchShiftApprovedById != null &&
                    this.model.operationalPlans[0]?.dispatchShiftApprovalDate != null;

                return hasPermission && validStatus && fieldsCheck;  
            }
        }
        return false;
    }

    canReceiveCaptain() {        
        if (this.model && this.model.documentTypeId === VesselVisitDocumentTypeCodes.OP) { 
            if (this.model.operationalPlans?.length > 0 && this.model.operationalPlans[0].captainReceivedDate == null) {
                const hasPermission = this.user.hasPermission(VesselVisitDocumentPermissions.Action.ReceiveCaptain);           
                const sameOrganization = this.model?.createdBy?.organizationId === this.user.organizationId;            
                const validStatus = 
                    this.model?.statusId === VesselVisitDocumentStatusCodes.Approved;

                const fieldsCheck =
                    this.model.operationalPlans[0]?.agencyApprovedById != null &&
                    this.model.operationalPlans[0]?.agencyApprovalDate != null &&
                    this.model.operationalPlans[0]?.dispatchApprovedById != null &&
                    this.model.operationalPlans[0]?.dispatchApprovalDate != null &&
                    this.model.operationalPlans[0]?.dispatchShiftApprovedById != null &&
                    this.model.operationalPlans[0]?.dispatchShiftApprovalDate != null;

                return hasPermission && validStatus && fieldsCheck;  
            }
        }
        return false;
    }

    async receive(receiveType: ReceiveOperationalPlanType) {
        var title = this.translateService.instant('Operational Plan Receipt Confirmation');
        var message = this.translateService.instant(`Are you sure you want to confirm receipt of the Operational Plan?
            
            By confirming, you acknowledge that the plan has been received and is ready for further processing?`);
        const data = await this.dialogService.form({
            title: title,
            message: message,
            properties: [{ name: 'remark', label: 'Remark' }]
        });
        if (!data) return;
        this.executeCommand('ReceiveOperationalPlan', { ReceiveType: receiveType, Remark: data.remark });
    }

    override executeCommand(commandName: string,data: any = {},customCallback: (response?: any) => any | Promise<void> = null): Promise<any> {
        // Call base method, but merge response into model
        return super.executeCommand(commandName, data, async (response) => {
            if (response) {
                this.model = response;
            }

            // Call custom callback if provided
            if (customCallback) {
                await customCallback(response);
            }
        });
    }

    canReject() {
        if (this.model
            && this.model.documentTypeId !== VesselVisitDocumentTypeCodes.AR
            && this.model.documentTypeId !== VesselVisitDocumentTypeCodes.OP) {            
            const sameOrganization = this.model?.createdBy?.organizationId === this.user.organizationId;
            const isAdmin = this.isAdminUser;
            const validStatus = 
                this.model?.statusId === VesselVisitDocumentStatusCodes.WaitingAgentApproval;

            return (sameOrganization || isAdmin) && validStatus;  
        }
        return false;
    }

    async reject() {
        const data = await this.dialogService.form({
            title: this.translateService.instant('Vessel Visit Document Rejection'),
            message: this.translateService.instant('Are you sure you want to reject this vessel visit?'),
            properties: [{ name: 'rejectionRemark', label: this.translateService.instant('Rejection Remark') }]
        });
        if (!data) return;
        this.executeCommand('RejectVesselVisitDocument', { rejectionRemark: data.rejectionRemark });
    }

    async cancelAccommodationRequest() {
        const data = await this.dialogService.form({
            title: this.translateService.instant('Cancel accommodation request'),
            message: this.translateService.instant(`Are you sure you want to cancel accommodation request?
                
                By canceling the accommodation request, the vessel visit will also be canceled.`),
            properties: []
        });
        if (!data) return;
        this.executeCommand('CancelAccommodationRequest', {  });
    }

    private getApprovePermissionsCount(): number {
        let count = 0;

        if (this.user.hasPermission(VesselVisitDocumentPermissions.Action.Approve)){
            return 3;
        };


        if (this.user.hasPermission(VesselVisitDocumentPermissions.Action.ApproveAgent)) count++;
        if (this.user.hasPermission(VesselVisitDocumentPermissions.Action.ApproveDispatch)) count++;
        if (this.user.hasPermission(VesselVisitDocumentPermissions.Action.ApproveDispatchShift)) count++;

        return count;
    }

    private resolveApproveLabel(specificLabel: string): string {
        const permissionCount = this.getApprovePermissionsCount();

        return permissionCount > 1 ? specificLabel : 'Approve';
    }

    private getReceivePermissionsCount(): number {
        let count = 0;

        if (this.user.hasPermission(VesselVisitDocumentPermissions.Action.ReceiveFSPD)) count++;
        if (this.user.hasPermission(VesselVisitDocumentPermissions.Action.ReceiveCaptain)) count++;

        return count;
    }

    private resolveReceiveLabel(specificLabel: string): string {
        const permissionCount = this.getReceivePermissionsCount();

        return permissionCount > 1 ? specificLabel : 'Receive';
    }

    override async saveChanges(customData?: any) {
        const proposals = this.model.accommodationRequests?.[0]?.berthProposals ?? [];

        const savedItems = proposals.filter(x => x.id > 0);
        const hasUnsaved = proposals.some(x => !x.id || x.id === 0);

        const allSavedRejected =
            savedItems.length > 0 &&
            savedItems.every(x => x.accepted === false);

        if (!hasUnsaved && allSavedRejected) {
            this.toastrService.error(this.translate.instant('For a rejected berth proposal, it is necessary to enter a new alternative proposal before saving.'));
            return;
        }

        return await super.saveChanges(customData);
    }  

    get isAddAttachmentDisabled(): boolean {        
        if(this.model.statusId !== VesselVisitDocumentStatusCodes.InProgress)
            return true;

        return false;        
    }

    override isSaveButtonDisabled(): boolean {
        if(this.model.statusId == VesselVisitDocumentStatusCodes.InProgress){

            if(this.model.documentTypeId!==VesselVisitDocumentTypeCodes.AR){
            
                return false;
            
            }else{

                if(this.model.accommodationRequests?.length>0){
                    const accommodationRequest = this.model.accommodationRequests[0];
                    //check if required fields are set and atleast one berth proposal is created
                    if(
                        (accommodationRequest.vesselAgencyId===undefined || accommodationRequest.vesselAgencyId==null) ||
                        (accommodationRequest.captain===undefined || accommodationRequest.captain==null || accommodationRequest.captain.length==0) ||
                        accommodationRequest.berthProposals?.length==0)
                    {
                            return true;
                    }
                }                            

            }
        }


        return super.isSaveButtonDisabled();
    }
}
